import { useSignalR } from '~/composables/useSignalR';

// Module scope: original handler -> wrapped (logging) handler, per event. off() used to
// pass the ORIGINAL handler to signalR.off, which never matched the registered wrapper —
// so every messaging mount/unmount stacked another live handler and each incoming event
// fired N duplicate reloads. The registry lets off() remove the exact wrapper.
const wrappedHandlerRegistry = new Map(); // eventName -> WeakMap(original -> wrapped)

export const useMessagingSignalR = () => {
    const signalR = useSignalR();
    const hubPath = '/signalr/v1';

    // Event names from backend hub
    const EVENTS = {
        NEW_MESSAGE: 'newmessage',
        MESSAGE_READ: 'messageread',
        USER_TYPING: 'usertyping',
        CONVERSATION_UPDATED: 'conversationupdated',
        SYNC_REQUIRED: 'syncrequired'
    };

    /**
     * Connect to SignalR hub
     */
    const connect = async () => {
        await signalR.connect(hubPath);
    };

    /**
     * Disconnect from SignalR hub
     */
    const disconnect = async () => {
        await signalR.disconnect();
    };

    /**
     * Join a conversation group to receive real-time updates
     * @param {string} conversationId - GUID of conversation
     */
    const joinConversation = async (conversationId) => {
        if (!signalR.isConnected.value) {
            console.warn('Cannot join conversation - SignalR not connected');
            return;
        }
        try {
            await signalR.send('JoinConversation', conversationId);
            console.info(`Joined conversation: ${conversationId}`);
        } catch (error) {
            console.error('Error joining conversation:', error);
        }
    };

    /**
     * Leave a conversation group
     * @param {string} conversationId - GUID of conversation
     */
    const leaveConversation = async (conversationId) => {
        if (!signalR.isConnected.value) return;
        try {
            await signalR.send('LeaveConversation', conversationId);
            console.info(`Left conversation: ${conversationId}`);
        } catch (error) {
            console.error('Error leaving conversation:', error);
        }
    };

    /**
     * Broadcast a new message to conversation participants
     * @param {string} conversationId
     * @param {Object} message - Message object
     */
    const broadcastMessage = async (conversationId, message) => {
        console.log('[SignalR SEND] broadcastMessage START', {
            conversationId,
            messageId: message?.id,
            messageType: message?.messageType,
            content: message?.content?.substring(0, 50),
            timestamp: new Date().toISOString(),
            isConnected: signalR.isConnected.value
        });

        if (!signalR.isConnected.value) {
            console.warn('[SignalR SEND] broadcastMessage SKIPPED - Not connected');
            return;
        }

        try {
            await signalR.send('SendMessage', conversationId, message);
            console.log('[SignalR SEND] broadcastMessage SUCCESS', {
                conversationId,
                messageId: message?.id,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('[SignalR SEND] broadcastMessage ERROR', {
                conversationId,
                messageId: message?.id,
                error: error.message || error,
                timestamp: new Date().toISOString()
            });
        }
    };

    /**
     * Notify conversation that a message was read
     * @param {string} conversationId
     * @param {string} messageId
     * @param {string} userId
     */
    const notifyMessageRead = async (conversationId, messageId, userId) => {
        console.log('[SignalR SEND] notifyMessageRead START', {
            conversationId,
            messageId,
            userId,
            timestamp: new Date().toISOString(),
            isConnected: signalR.isConnected.value
        });

        if (!signalR.isConnected.value) {
            console.warn('[SignalR SEND] notifyMessageRead SKIPPED - Not connected');
            return;
        }

        try {
            await signalR.send('MarkMessageAsRead', conversationId, messageId, userId);
            console.log('[SignalR SEND] notifyMessageRead SUCCESS', {
                conversationId,
                messageId,
                userId,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('[SignalR SEND] notifyMessageRead ERROR', {
                conversationId,
                messageId,
                userId,
                error: error.message || error,
                timestamp: new Date().toISOString()
            });
        }
    };

    /**
     * Send typing indicator to conversation
     * @param {string} conversationId
     * @param {boolean} isTyping
     */
    const sendTypingIndicator = async (conversationId, isTyping) => {
        console.log('[SignalR SEND] sendTypingIndicator', {
            conversationId,
            isTyping,
            timestamp: new Date().toISOString(),
            isConnected: signalR.isConnected.value
        });

        if (!signalR.isConnected.value) {
            console.warn('[SignalR SEND] sendTypingIndicator SKIPPED - Not connected');
            return;
        }

        try {
            await signalR.send('TypingIndicator', conversationId, isTyping);
        } catch (error) {
            console.error('[SignalR SEND] sendTypingIndicator ERROR', {
                conversationId,
                isTyping,
                error: error.message || error,
                timestamp: new Date().toISOString()
            });
        }
    };

    const listen = (eventName, handler) => {
        // X63: never from a server render (useSignalR().on is a no-op there, and a
        // wrapper kept here would hold the caller's closure for the process's life).
        if (import.meta.server) return;
        if (!wrappedHandlerRegistry.has(eventName)) {
            wrappedHandlerRegistry.set(eventName, new WeakMap());
        }
        const forEvent = wrappedHandlerRegistry.get(eventName);
        if (forEvent.has(handler)) return; // this handler is already listening

        const wrappedHandler = (...args) => {
            console.log(`[SignalR RECEIVE] ${eventName}`, {
                payload: args[0]?.id ?? args[0]?.conversationId ?? args[0] ?? null,
                timestamp: new Date().toISOString()
            });
            handler(...args);
        };
        forEvent.set(handler, wrappedHandler);
        signalR.on(eventName, wrappedHandler);
    };

    const unlisten = (eventName, handler) => {
        const wrapped = wrappedHandlerRegistry.get(eventName)?.get(handler);
        if (wrapped) {
            signalR.off(eventName, wrapped);
            wrappedHandlerRegistry.get(eventName).delete(handler);
        }
    };

    const onNewMessage = (handler) => listen(EVENTS.NEW_MESSAGE, handler);
    const offNewMessage = (handler) => unlisten(EVENTS.NEW_MESSAGE, handler);

    const onMessageRead = (handler) => listen(EVENTS.MESSAGE_READ, handler);
    const offMessageRead = (handler) => unlisten(EVENTS.MESSAGE_READ, handler);

    const onUserTyping = (handler) => listen(EVENTS.USER_TYPING, handler);
    const offUserTyping = (handler) => unlisten(EVENTS.USER_TYPING, handler);

    const onConversationUpdated = (handler) => listen(EVENTS.CONVERSATION_UPDATED, handler);
    const offConversationUpdated = (handler) => unlisten(EVENTS.CONVERSATION_UPDATED, handler);

    const onSyncRequired = (handler) => listen(EVENTS.SYNC_REQUIRED, handler);
    const offSyncRequired = (handler) => unlisten(EVENTS.SYNC_REQUIRED, handler);

    return {
        // Connection
        connect,
        disconnect,
        isConnected: signalR.isConnected,

        // Conversation groups
        joinConversation,
        leaveConversation,

        // Invoke server methods
        broadcastMessage,
        notifyMessageRead,
        sendTypingIndicator,

        // Event listeners
        onNewMessage,
        offNewMessage,
        onMessageRead,
        offMessageRead,
        onUserTyping,
        offUserTyping,
        onConversationUpdated,
        offConversationUpdated,
        onSyncRequired,
        offSyncRequired
    };
};
